import React, { useState, useRef, useEffect, useCallback } from 'react';
import './collapsibleCard.scss';

// ChevronDown icon component
const ChevronDown = ({ className = '' }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

// CollapsibleCardHeader component
const CollapsibleCardHeader = ({
  children,
  onClick,
  isCollapsed,
  disabled = false,
  className = '',
  showArrow = true,
  arrowPosition = 'end', // 'end' | 'top'
  collapseDirection = 'down', // 'down' | 'up'
}) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onClick?.();
    }
  };

  const getChevronClass = () => {
    let chevronClass = 'collapsible-card-chevron';

    if (arrowPosition === 'top') {
      chevronClass += ' position-top';
    }

    if (collapseDirection === 'up') {
      chevronClass += ' direction-up';
    }

    if (!isCollapsed) {
      chevronClass += ' expanded';
    }

    return chevronClass;
  };

  return (
    <div
      className={`collapsible-card-header ${className} ${
        disabled ? 'disabled' : ''
      } ${arrowPosition === 'top' ? 'arrow-position-top' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : 'none'}
      tabIndex={onClick && !disabled ? 0 : -1}
      aria-expanded={onClick ? !isCollapsed : undefined}
    >
      {showArrow && onClick && (
        <div className="arrow-container">
          <ChevronDown className={getChevronClass()} />
        </div>
      )}
    </div>
  );
};

// CollapsibleCardContent component
const CollapsibleCardContent = ({
  children,
  isCollapsed,
  className = '',
  maxHeight = null,
  autoHeight = false,
  collapseDirection = 'down', // 'down' | 'up'
  disabled = false,
}) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Function to calculate height
  const calculateHeight = useCallback(() => {
    if (contentRef.current) {
      if (isCollapsed) {
        return 0;
      } else if (autoHeight) {
        return contentRef.current.scrollHeight;
      } else if (maxHeight) {
        const contentHeight = contentRef.current.scrollHeight;
        return Math.min(contentHeight, maxHeight);
      } else {
        return contentRef.current.scrollHeight;
      }
    }
    return 0;
  }, [isCollapsed, autoHeight, maxHeight]);

  // Initial setup effect
  useEffect(() => {
    if (contentRef.current && !isInitialized) {
      if (disabled) {
        // Skip animation when disabled - set height immediately
        const newHeight = calculateHeight();
        setHeight(newHeight);
        setIsInitialized(true);
      } else {
        // Use a small delay to ensure content is fully rendered
        const timeoutId = setTimeout(() => {
          const newHeight = calculateHeight();
          setHeight(newHeight);
          setIsInitialized(true);
        }, 0);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    isCollapsed,
    autoHeight,
    maxHeight,
    isInitialized,
    calculateHeight,
    disabled,
  ]);

  // Height update effect for subsequent changes
  useEffect(() => {
    if (isInitialized) {
      const newHeight = calculateHeight();
      setHeight(newHeight);
    }
  }, [
    isCollapsed,
    children,
    maxHeight,
    autoHeight,
    isInitialized,
    calculateHeight,
  ]);

  // ResizeObserver for dynamic content changes
  useEffect(() => {
    if (!isCollapsed && contentRef.current && isInitialized) {
      const resizeObserver = new ResizeObserver(() => {
        const newHeight = calculateHeight();
        setHeight(newHeight);
      });

      resizeObserver.observe(contentRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [
    children,
    maxHeight,
    autoHeight,
    isCollapsed,
    isInitialized,
    calculateHeight,
  ]);

  const contentStyle = {
    height: `${height}px`,
    maxHeight: maxHeight && !autoHeight ? `${maxHeight}px` : undefined,
  };

  const contentClasses = [
    'collapsible-card-content',
    `direction-${collapseDirection}`,
    disabled ? 'disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={contentClasses}
      style={contentStyle}
      aria-hidden={isCollapsed}
    >
      <div ref={contentRef} className="collapsible-card-content-inner">
        {children}
      </div>
    </div>
  );
};

// CollapsibleCardFooter component
const CollapsibleCardFooter = ({ children, className = '' }) => {
  if (!children) return null;

  return (
    <div className={`collapsible-card-footer ${className}`}>{children}</div>
  );
};

// Main CollapsibleCard component
const CollapsibleCard = ({
  children,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onToggle,
  className = '',
  disabled = false,
  variant = 'default', // 'default', 'glass', 'bordered'
  size = 'default', // 'sm', 'default', 'lg'
  arrowPosition = 'end', // 'end' | 'top'
  collapseDirection = 'down', // 'down' | 'up'
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  const isControlled = controlledCollapsed !== undefined;
  const isCollapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const handleToggle = () => {
    if (disabled) return;

    const newCollapsed = !isCollapsed;

    if (!isControlled) {
      setInternalCollapsed(newCollapsed);
    }

    onToggle?.(newCollapsed);
  };

  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === CollapsibleCardHeader) {
        return React.cloneElement(child, {
          isCollapsed,
          onClick: child.props.onClick || handleToggle,
          disabled: disabled || child.props.disabled,
          arrowPosition: child.props.arrowPosition || arrowPosition,
          collapseDirection: child.props.collapseDirection || collapseDirection,
        });
      }
      if (child.type === CollapsibleCardContent) {
        return React.cloneElement(child, {
          isCollapsed,
          collapseDirection: child.props.collapseDirection || collapseDirection,
          disabled: disabled || child.props.disabled,
        });
      }
    }
    return child;
  });

  const cardClasses = [
    'collapsible-card',
    `collapsible-card--${variant}`,
    `collapsible-card--${size}`,
    `arrow-position-${arrowPosition}`,
    `direction-${collapseDirection}`,
    className,
    disabled ? 'disabled' : '',
    isCollapsed ? 'collapsed' : 'expanded',
  ]
    .filter(Boolean)
    .join(' ');

  return <div className={cardClasses}>{enhancedChildren}</div>;
};

// Compound component exports
CollapsibleCard.Header = CollapsibleCardHeader;
CollapsibleCard.Content = CollapsibleCardContent;
CollapsibleCard.Footer = CollapsibleCardFooter;

export default CollapsibleCard;
